/**
 * What the Keeper Calculator screen shows, worked out away from the markup.
 *
 * The screen answers one question — what does he cost me next year — and every figure in that
 * answer comes from the rules engine: `keeperCost`, `keeperProjection`, `assessLuxuryTax`,
 * `penaltyRound`. This module is the layer between them and the component. It does no
 * arithmetic of its own on a round or a dollar; it decides which engine call to make, and what
 * the answer is CALLED on screen.
 *
 * That division is the acceptance test. If a number on the Calculator disagrees with the engine,
 * the bug is here or in the component, never in the engine — so nothing below may compute a cost,
 * a round or a tax. Where a figure appears, it was returned by one of the four functions above.
 */

import type { LeagueConfig } from './config.js';
import {
  GRID_ROUNDS, NOT_ELIGIBLE, maxKeeperYears, penaltyRound,
} from './grid.js';
import {
  type Acquisition, type KeeperCost, type KeeperRecord, effectiveRoundValue,
} from './keepers.js';

/* ------------------------------------------------------------------ what the form holds */

/**
 * The eight facts the engine needs about a player, and the only state the form carries.
 *
 * Deliberately the engine's own vocabulary rather than the screen's. A field named after a
 * control ("roundStepper") is a field that stops matching the rule it feeds the moment the
 * control changes shape; these are named for what `KeeperRecord` calls them, so `recordOf` is a
 * rename and nothing more.
 */
export interface CalcInputs {
  acquisition: Acquisition;
  roundValue: number;
  yearsKept: number;
  neverDropped: boolean;
  onSeasonEndingIR: boolean;
  onIRBeforeDraft: boolean;
  /** Farm graduate: the NFL round that took him, or null for undrafted. */
  nflDraftRound: number | null;
  farmFailedToMakeRoster: boolean;
  /** True once the one-time sleeper waiver has been spent on him. */
  sleeperUsed: boolean;
  /** For display only — the round he was originally drafted in. Never priced off. */
  originalRound: number | null;
}

/**
 * The slice of a keeper-build row the picker reads.
 *
 * Same shape as `keeperRow.KeeperSource` where the two overlap, and wider where the engine needs
 * more: `yearsKept`, `originalRound`, `sleeperUsed` and `onSeasonEndingIR` were added to
 * `keepers-2026.json` for this screen, because a stored `cost` answers today's question and this
 * screen asks yesterday's and tomorrow's.
 */
export interface CalcSource {
  player: string;
  team: string;
  pos?: string;
  acq?: string;
  val: number;
  yearsKept?: number;
  originalRound?: number | null;
  sleeperUsed?: boolean;
  onSeasonEndingIR?: boolean;
  dropped?: boolean;
  nflRd?: number | null;
  mandatory?: boolean;
}

/**
 * THE BOUNDS COME FROM THE GRID, NOT FROM THE STEPPER.
 *
 * A stepper that stops at 20 because somebody typed 20 into a component is a stepper that keeps
 * stopping at 20 after the draft expands. Both ends are read off the rule instead:
 *
 *   `penaltyRound` throws below 1, so 1 is the floor.
 *   The Grid's printed table runs to Round 19 and the floor rule maps everything from 16 on to a
 *   12th, so one row past the table is the last value that can mean anything new — Round 20 is
 *   the UDFA slot in a draft one round longer than this one. That is where the ceiling sits.
 *
 * `maxKeeperYears` gives the other pair: nobody has served more keeper years than the longest run
 * the Grid allows, which is four from a 13th-round value or later.
 */
export const ROUND_MIN = 1;
export const ROUND_MAX = GRID_ROUNDS + 1;
export const YEARS_MIN = 0;
export const YEARS_MAX = maxKeeperYears(GRID_ROUNDS);
/** The NFL draft has seven rounds; `farmGraduationRound` refuses anything else. */
export const NFL_ROUND_MIN = 1;
export const NFL_ROUND_MAX = 7;

/** Clamp to a stepper's bounds. The only arithmetic in this file, and it is not on a cost. */
export const step = (value: number, by: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, value + by));

/**
 * The hypothetical's starting point: an ordinary Round 12 pick, never kept, never dropped.
 *
 * Round 12 rather than Round 1 because the question the manual form exists for is "what would a
 * late pick cost me", and Round 12 is the middle of the band where the answer is interesting —
 * it is also the value the design draws. A Round 1 default answers "Not eligible" before the
 * manager has touched anything, which reads as the form being broken.
 */
export const MANUAL_INPUTS: CalcInputs = {
  acquisition: 'drafted',
  roundValue: 12,
  yearsKept: 0,
  neverDropped: true,
  onSeasonEndingIR: false,
  onIRBeforeDraft: false,
  nflDraftRound: 3,
  farmFailedToMakeRoster: false,
  sleeperUsed: false,
  originalRound: null,
};

/**
 * Fill the form from a player the league already knows about.
 *
 * This is the whole reason the picker leads the screen: acquisition, round value, years served
 * and drop history are all recorded, and asking a manager to retype them is asking him to get one
 * of them wrong. `onIRBeforeDraft` is the one fact the build does not carry — nobody records, per
 * player, whether he was on NFL IR on the day of last year's fantasy draft — so it starts false
 * and stays a toggle the manager can set.
 */
export function inputsFrom(row: CalcSource): CalcInputs {
  return {
    acquisition: (row.acq as Acquisition | undefined) ?? 'drafted',
    roundValue: row.val,
    yearsKept: row.yearsKept ?? 0,
    neverDropped: row.dropped !== true,
    onSeasonEndingIR: row.onSeasonEndingIR === true,
    onIRBeforeDraft: false,
    nflDraftRound: row.nflRd ?? null,
    farmFailedToMakeRoster: false,
    sleeperUsed: row.sleeperUsed === true,
    originalRound: row.originalRound ?? null,
  };
}

/** The form as the engine reads it. A rename, not a calculation. */
export function recordOf(
  inputs: CalcInputs, season: number, playerId = 'preview', teamId = 'preview',
): KeeperRecord {
  return {
    playerId,
    teamId,
    season,
    acquisition: inputs.acquisition,
    currentRoundValue: inputs.roundValue,
    yearsKept: inputs.yearsKept,
    originalRound: inputs.originalRound,
    sleeperUsed: inputs.sleeperUsed,
    neverDropped: inputs.neverDropped,
    onSeasonEndingIR: inputs.onSeasonEndingIR,
    onIRBeforeDraft: inputs.onIRBeforeDraft,
    nflDraftRound: inputs.nflDraftRound,
    farmFailedToMakeRoster: inputs.farmFailedToMakeRoster,
  };
}

/**
 * The round a roster line should PRINT, which is not always the round it stores.
 *
 * A waiver pickup carries `currentRoundValue: 0` — he was not drafted, so there is no round to
 * record — and the Grid reads him as one past the last round of the draft instead (2022). A
 * picker printing the stored value said "Rd 0" against eleven names, which reads as a bug and
 * sorted the cheapest players to the top of the list as though they were the dearest.
 *
 * `effectiveRoundValue` is the engine's own resolution of the same question, so the list is
 * ordered and labelled by what the rules actually charge.
 */
export function displayRound(row: CalcSource, config: LeagueConfig): number {
  return effectiveRoundValue(recordOf(inputsFrom(row), DISPLAY_SEASON), config);
}

/** As with `gridReadout`: `effectiveRoundValue` never reads the season. */
const DISPLAY_SEASON = 0;

/* ---------------------------------------------------------------------------- the verdict */

export type VerdictState = 'sleeper' | 'normal' | 'ineligible';

export interface Verdict {
  state: VerdictState;
  /** `Cost to keep in 2026` — the kicker above the line. */
  kicker: string;
  /** The 30px line itself. */
  line: string;
  /** The engine's own explanation, unedited. */
  why: string;
}

/**
 * Three states, three sentences, and the sentence is chosen by the engine's answer rather than by
 * re-deriving anything. `Free — sleeper year` says the two things at once that `FREE` alone did
 * not: it costs nothing, and the reason is the one-time reward rather than a quirk of the Grid.
 */
export function verdictOf(cost: KeeperCost, keepSeason: number): Verdict {
  const kicker = `Cost to keep in ${keepSeason}`;
  if (!cost.eligible) {
    return { state: 'ineligible', kicker, line: 'Not eligible', why: cost.explanation };
  }
  if (cost.isSleeperYear) {
    return { state: 'sleeper', kicker, line: 'Free — sleeper year', why: cost.explanation };
  }
  return {
    state: 'normal',
    kicker,
    line: `Your Round ${cost.costRound} pick`,
    why: cost.explanation,
  };
}

/* ------------------------------------------------------------------------ the Grid readout */

/** The season `gridReadout` hands `recordOf`; nothing downstream of it reads the field. */
const READOUT_SEASON = 0;

export interface GridReadout {
  from: number;
  to: number | null;
  maxYears: number;
  text: string;
}

/**
 * `Round 12 → Round 8 · max 3 yr`, and `Not Eligible` when there is no earlier pick to forfeit.
 *
 * Reads the EFFECTIVE round value, not the number in the stepper. A waiver pickup in his first
 * year is priced at one past the last round of the draft (2022) and a farm graduate at his
 * graduation round, so a readout built off the stepper would say "Round 12" for a player the Grid
 * is reading as a 17. `effectiveRoundValue` is the engine's own resolution of that.
 *
 * `max N yr` is `maxKeeperYears`, which walks the Grid, rather than the published Max Keeper
 * Length column — the two are asserted equal by `tests/grid.test.ts`, and deriving it means a
 * Grid edit moves the readout instead of leaving it agreeing with an old table.
 */
export function gridReadout(inputs: CalcInputs, config: LeagueConfig): GridReadout {
  // `effectiveRoundValue` reads acquisition, years kept, round value and the NFL round; the
  // season is not one of them, so any value stands in for it here.
  const from = effectiveRoundValue(recordOf(inputs, READOUT_SEASON), config);
  const to = penaltyRound(from);
  if (to === NOT_ELIGIBLE) {
    return { from, to: null, maxYears: 0, text: `Round ${from} → Not Eligible` };
  }
  const maxYears = maxKeeperYears(from);
  return { from, to, maxYears, text: `Round ${from} → Round ${to} · max ${maxYears} yr` };
}

/* -------------------------------------------------------------------------- the year tiles */

export type TileKind =
  /** A keeper year the sleeper reward pays for. */
  | 'free'
  /** A keeper year with a pick attached. */
  | 'cost'
  /** The first season after his run — the tile that says when it ends. */
  | 'ended'
  /** Past that. Nothing to say. */
  | 'none';

export interface YearTile {
  season: number;
  kind: TileKind;
  costRound: number | null;
  /** The season being decided right now — it takes the verdict's wash. */
  current: boolean;
}

/**
 * Four seasons across, one tile each.
 *
 * The projection is as long as his remaining run, which is one to four years; the row is always
 * four wide, because a row that changes width with the answer cannot be compared against the row
 * beside it in `1c`. What fills the gap is the interesting part:
 *
 *   THE FIRST SEASON PAST THE RUN IS MARKED, the rest are blank. A manager weighing a Round 4
 *   keeper is deciding how many seasons he buys, and "he is gone after this one" is the fact that
 *   decides it — an em dash in that slot states it only by absence. So the tile immediately after
 *   the last keeper year reads `Last yr` in amber, and everything past it is the em dash.
 *
 * This is the reading of the design that fits both drawings. The desktop frame shows a four-year
 * run with a cost in all four tiles and no amber anywhere — so the marker cannot be on the last
 * keeper year, or the fourth tile would carry it. The compare frame shows a one-year run with the
 * cost in 2026, amber in 2027 and dashes after — so the marker sits one past the end. Flagged for
 * Tim: the brief's sentence ("a final year reads Last yr") reads the other way, and the two
 * frames are what settled it.
 *
 * A player who cannot be kept at all has no run and therefore no end to mark: all four are blank.
 */
export function yearTiles(
  projection: ReadonlyArray<{ season: number; costRound: number | null; isSleeperYear: boolean }>,
  firstSeason: number,
  span = 4,
): YearTile[] {
  const out: YearTile[] = [];
  for (let i = 0; i < span; i++) {
    const season = firstSeason + i;
    const year = projection[i];
    if (year) {
      out.push({
        season,
        kind: year.isSleeperYear ? 'free' : 'cost',
        costRound: year.costRound,
        current: i === 0,
      });
      continue;
    }
    const ends = projection.length > 0 && i === projection.length;
    out.push({ season, kind: ends ? 'ended' : 'none', costRound: null, current: false });
  }
  return out;
}

/**
 * What a tile says. `Rd 8` on a phone, `Round 8` where there is room for it.
 *
 * Formatting, not arithmetic — `costRound` arrived from `keeperProjection` and is printed as it
 * came.
 */
export function tileLabel(tile: YearTile, wide: boolean): string {
  switch (tile.kind) {
    case 'free': return 'Free';
    case 'ended': return 'Last yr';
    case 'none': return '—';
    default: return `${wide ? 'Round' : 'Rd'} ${tile.costRound}`;
  }
}

/**
 * `Rounds 8, 5, 2` — the picks the whole run costs, and whether the run ends inside the window.
 *
 * The compare view's per-card summary. Two cards side by side is a lookup; what turns it into a
 * decision is the four-year total, because the cheaper player this season is regularly the dearer
 * one over the run. Free years are not listed — they cost no pick, and printing "Free" in a list
 * of rounds reads as a round called Free.
 */
export function fourYearCost(
  projection: ReadonlyArray<{ costRound: number | null; isSleeperYear: boolean }>,
  span = 4,
): string {
  if (projection.length === 0) return 'Not eligible';
  const rounds = projection
    .filter((p) => !p.isSleeperYear && p.costRound !== null)
    .map((p) => p.costRound);
  const ends = projection.length < span;
  if (rounds.length === 0) return ends ? 'Free, then done' : 'Free every year';
  return `Rounds ${rounds.join(', ')}${ends ? ', then done' : ''}`;
}

/* ------------------------------------------------------------------------------- compare */

export interface ComparePlayer {
  player: string;
  cost: KeeperCost;
}

/**
 * `costs your Round 2 pick and nothing else` — what keeping the whole comparison costs this year.
 *
 * The line the summary bar is for. A manager comparing two keepers is not choosing between two
 * verdicts, he is asking what happens if he takes both, and the answer is a list of picks he will
 * not be making. Sleeper years contribute nothing to it, which is exactly the point of them.
 */
export function pairCostLine(players: readonly ComparePlayer[]): string {
  const rounds = players
    .filter((p) => p.cost.eligible && !p.cost.isSleeperYear && p.cost.costRound !== null)
    .map((p) => p.cost.costRound!);
  if (rounds.length === 0) return 'costs you no picks at all';
  if (rounds.length === 1) return `costs your Round ${rounds[0]} pick and nothing else`;
  const last = rounds[rounds.length - 1];
  const rest = rounds.slice(0, -1);
  return `costs your Round ${rest.join(', Round ')} and Round ${last} picks`;
}
